// src/components/users/UserFilters.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchDepartments, 
  fetchTeams, 
  fetchManagers,
  fetchEmployeeTypes,
  fetchEmploymentStatuses 
} from '../../store/actions/referenceActions';

const UserFilters = ({ filters, onFilterChange }) => {
  const dispatch = useDispatch();
  const { departments, teams, managers, employeeTypes, employmentStatuses, loading } = 
    useSelector((state) => state.reference);
  
  const [localFilters, setLocalFilters] = useState(filters);
  const [isExpanded, setIsExpanded] = useState(false);

  // Styles object
  const styles = {
    container: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      padding: '1.5rem',
      marginBottom: '1.5rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1rem',
      cursor: 'pointer',
    },
    title: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#111827',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    titleIcon: {
      width: '1.25rem',
      height: '1.25rem',
      color: '#6b7280',
    },
    expandButton: {
      background: 'none',
      border: 'none',
      color: '#6b7280',
      cursor: 'pointer',
      padding: '0.25rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'transform 0.2s',
    },
    expandIcon: {
      width: '1.25rem',
      height: '1.25rem',
      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
      transition: 'transform 0.2s',
    },
    filterGrid: {
      display: isExpanded ? 'grid' : 'none',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1rem',
      marginBottom: '1.5rem',
    },
    filterGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem',
    },
    filterLabel: {
      fontSize: '0.75rem',
      fontWeight: 500,
      color: '#6b7280',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
    filterInput: {
      width: '100%',
      padding: '0.5rem 0.75rem',
      fontSize: '0.875rem',
      color: '#111827',
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'all 0.2s',
    },
    filterSelect: {
      width: '100%',
      padding: '0.5rem 0.75rem',
      fontSize: '0.875rem',
      color: '#111827',
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'all 0.2s',
      cursor: 'pointer',
      appearance: 'none',
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
      backgroundPosition: 'right 0.5rem center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '1.5em 1.5em',
      paddingRight: '2.5rem',
    },
    filterInputFocus: {
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
    },
    filterSelectFocus: {
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
    },
    filterInputDisabled: {
      backgroundColor: '#f3f4f6',
      cursor: 'not-allowed',
      opacity: 0.6,
    },
    actionContainer: {
      display: isExpanded ? 'flex' : 'none',
      justifyContent: 'flex-end',
      gap: '1rem',
      marginTop: '1rem',
      paddingTop: '1rem',
      borderTop: '1px solid #e5e7eb',
    },
    applyButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    applyButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    resetButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#4b5563',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    resetButtonHover: {
      backgroundColor: '#374151',
    },
    activeFilterBadge: {
      display: isExpanded ? 'none' : 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.25rem 0.5rem',
      backgroundColor: '#dbeafe',
      color: '#1e40af',
      fontSize: '0.75rem',
      fontWeight: 500,
      borderRadius: '0.25rem',
      marginLeft: '0.5rem',
    },
    loadingIndicator: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      color: '#6b7280',
      fontSize: '0.875rem',
    },
    loadingSpinner: {
      width: '1rem',
      height: '1rem',
      border: '2px solid #e5e7eb',
      borderTopColor: '#3b82f6',
      borderRadius: '50%',
      animation: 'spin 0.6s linear infinite',
    },
    quickFilterContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
      marginTop: '0.5rem',
    },
    quickFilterChip: {
      padding: '0.25rem 0.75rem',
      backgroundColor: '#f3f4f6',
      color: '#374151',
      fontSize: '0.75rem',
      fontWeight: 500,
      borderRadius: '9999px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      border: '1px solid transparent',
    },
    quickFilterChipActive: {
      backgroundColor: '#dbeafe',
      color: '#1e40af',
      borderColor: '#3b82f6',
    },
    quickFilterChipHover: {
      backgroundColor: '#e5e7eb',
    },
    filterCount: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '1.25rem',
      height: '1.25rem',
      padding: '0 0.25rem',
      backgroundColor: '#ef4444',
      color: '#ffffff',
      fontSize: '0.75rem',
      fontWeight: 600,
      borderRadius: '9999px',
      marginLeft: '0.5rem',
    },
  };

  useEffect(() => {
    dispatch(fetchDepartments());
    dispatch(fetchTeams());
    dispatch(fetchManagers());
    dispatch(fetchEmployeeTypes());
    dispatch(fetchEmploymentStatuses());
  }, [dispatch]);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setLocalFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    onFilterChange(localFilters);
    setIsExpanded(false);
  }, [localFilters, onFilterChange]);

  const handleReset = useCallback(() => {
    const resetFilters = {
      search: '',
      department: '',
      team: '',
      employmentStatus: '',
      employeeType: '',
      isActive: '',
      managerId: '',
    };
    setLocalFilters(resetFilters);
    onFilterChange(resetFilters);
  }, [onFilterChange]);

  const handleQuickFilter = useCallback((type, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [type]: value,
    }));
    onFilterChange({
      ...localFilters,
      [type]: value,
    });
  }, [localFilters, onFilterChange]);

  const getActiveFilterCount = () => {
    let count = 0;
    if (localFilters.department) count++;
    if (localFilters.team) count++;
    if (localFilters.employmentStatus) count++;
    if (localFilters.employeeType) count++;
    if (localFilters.isActive) count++;
    if (localFilters.managerId) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  const employmentStatusOptions = [
    { value: 'ACTIVE', label: 'Actif' },
    { value: 'ON_LEAVE', label: 'En congé' },
    { value: 'SUSPENDED', label: 'Suspendu' },
    { value: 'TERMINATED', label: 'Résilié' },
    { value: 'RESIGNED', label: 'Démissionné' },
    { value: 'RETIRED', label: 'Retraité' },
    { value: 'PROBATION', label: 'Probation' },
  ];

  const employeeTypeOptions = [
    { value: 'FULL_TIME', label: 'Temps plein' },
    { value: 'PART_TIME', label: 'Temps partiel' },
    { value: 'CONTRACTOR', label: 'Contractor' },
    { value: 'INTERN', label: 'Intern' },
    { value: 'TEMPORARY', label: 'Temporaire' },
    { value: 'FREELANCER', label: 'Freelancer' },
    { value: 'CONSULTANT', label: 'Consultant' },
  ];

  const statusOptions = [
    { value: '', label: 'Tous' },
    { value: 'true', label: 'Actif' },
    { value: 'false', label: 'Inactif' },
  ];

  return (
    <div style={styles.container}>
      <div 
        style={styles.header}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div style={styles.title}>
          <svg style={styles.titleIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          Filtres
          {activeFilterCount > 0 && !isExpanded && (
            <span style={styles.activeFilterBadge}>
              {activeFilterCount} actif(s)
            </span>
          )}
          {activeFilterCount > 0 && (
            <span style={styles.filterCount}>
              {activeFilterCount}
            </span>
          )}
        </div>
        <button style={styles.expandButton}>
          <svg 
            style={styles.expandIcon} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Quick Filter Chips */}
      {!isExpanded && (
        <div style={styles.quickFilterContainer}>
          {localFilters.department && (
            <span style={{...styles.quickFilterChip, ...styles.quickFilterChipActive}}>
              Dept: {localFilters.department}
            </span>
          )}
          {localFilters.employmentStatus && (
            <span style={{...styles.quickFilterChip, ...styles.quickFilterChipActive}}>
              Status: {employmentStatusOptions.find(s => s.value === localFilters.employmentStatus)?.label}
            </span>
          )}
          {localFilters.employeeType && (
            <span style={{...styles.quickFilterChip, ...styles.quickFilterChipActive}}>
              Type: {employeeTypeOptions.find(t => t.value === localFilters.employeeType)?.label}
            </span>
          )}
          {localFilters.isActive && (
            <span style={{...styles.quickFilterChip, ...styles.quickFilterChipActive}}>
              {localFilters.isActive === 'true' ? 'Active' : 'Inactive'}
            </span>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={styles.filterGrid}>
          {/* Department Filter */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Department</label>
            <select
              name="department"
              value={localFilters.department || ''}
              onChange={handleChange}
              style={styles.filterSelect}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = styles.filterSelectFocus.borderColor;
                e.currentTarget.style.boxShadow = styles.filterSelectFocus.boxShadow;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <option value="">Tous les départements</option>
              {departments.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>
          </div>

          {/* Team Filter */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Équipe</label>
            <select
              name="team"
              value={localFilters.team || ''}
              onChange={handleChange}
              style={styles.filterSelect}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = styles.filterSelectFocus.borderColor;
                e.currentTarget.style.boxShadow = styles.filterSelectFocus.boxShadow;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <option value="">Toutes les équipes</option>
              {teams.map((team) => (
                <option key={team} value={team}>
                  {team}
                </option>
              ))}
            </select>
          </div>

          {/* Employment Status Filter */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Statut d'emploi</label>
            <select
              name="employmentStatus"
              value={localFilters.employmentStatus || ''}
              onChange={handleChange}
              style={styles.filterSelect}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = styles.filterSelectFocus.borderColor;
                e.currentTarget.style.boxShadow = styles.filterSelectFocus.boxShadow;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <option value="">All Statuses</option>
              {employmentStatusOptions.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          {/* Employee Type Filter */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Type d'employé</label>
            <select
              name="employeeType"
              value={localFilters.employeeType || ''}
              onChange={handleChange}
              style={styles.filterSelect}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = styles.filterSelectFocus.borderColor;
                e.currentTarget.style.boxShadow = styles.filterSelectFocus.boxShadow;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <option value="">All Types</option>
              {employeeTypeOptions.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Statut du compte</label>
            <select
              name="isActive"
              value={localFilters.isActive || ''}
              onChange={handleChange}
              style={styles.filterSelect}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = styles.filterSelectFocus.borderColor;
                e.currentTarget.style.boxShadow = styles.filterSelectFocus.boxShadow;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <option value="">Tous</option>
              <option value="true">Actif uniquement</option>
              <option value="false">Inactif uniquement</option>
            </select>
          </div>

          {/* Manager Filter */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Manager</label>
            <select
              name="managerId"
              value={localFilters.managerId || ''}
              onChange={handleChange}
              style={styles.filterSelect}
              disabled={loading}
              onFocus={(e) => {
                if (!loading) {
                  e.currentTarget.style.borderColor = styles.filterSelectFocus.borderColor;
                  e.currentTarget.style.boxShadow = styles.filterSelectFocus.boxShadow;
                }
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <option value="">Tous les managers</option>
              {managers.map((manager) => (
                <option key={manager.id} value={manager.id}>
                  {manager.name} - {manager.department}
                </option>
              ))}
            </select>
            {loading && (
              <div style={styles.loadingIndicator}>
                <div style={styles.loadingSpinner} />
                Chargement des managers...
              </div>
            )}
          </div>
        </div>

        <div style={styles.actionContainer}>
          <button
            type="button"
            onClick={handleReset}
            style={styles.resetButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.resetButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.resetButton.backgroundColor;
            }}
          >
            Reinitialiser
          </button>
          <button
            type="submit"
            style={styles.applyButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.applyButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.applyButton.backgroundColor;
            }}
          >
            Appliquer les filtres
          </button>
        </div>
      </form>

      {/* Add keyframe animations */}
      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default UserFilters;